<template>
  <section
    class="group flex h-full w-full min-w-0 flex-col gap-1 rounded-md border border-border bg-bg-surface/80 p-1 transition-[border-color,box-shadow] duration-120 ease-standard hover:border-accent/70 hover:shadow-glow-accent select-none"
    :class="isOffTarget ? 'border-danger ring-1 ring-danger/40 hover:shadow-glow-danger hover:border-danger' : ''"
    :data-group-id="group.id"
    :data-collapsed="group.collapsed ? 'true' : 'false'"
  >
    <header class="flex flex-col gap-1">
      <div class="flex items-center justify-between gap-2 rounded-full border border-border bg-bg-surface py-1 pl-2 pr-1.5">
        <span class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-accent font-mono text-xs font-bold text-accent">
          {{ index }}
        </span>
        <div v-if="!nameField.isEditing.value" class="flex min-w-0 flex-1 items-center">
          <button
            v-if="canEdit"
            type="button"
            class="min-w-0 flex-1 cursor-pointer truncate rounded-sm bg-transparent px-1 text-left font-display text-sm font-semibold tracking-[0.04em] transition-colors hover:text-accent focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
            :class="group.label === '' ? 'italic text-text-muted hover:text-text-muted' : 'text-text-primary'"
            :title="group.label === '' ? messages.group.setName(index) : group.label"
            :aria-label="messages.group.rename(group.label === '' ? index : group.label)"
            @click="nameField.startEdit"
          >{{ displayLabel }}</button>
          <span
            v-else
            class="min-w-0 flex-1 truncate px-1 text-left font-display text-sm font-semibold tracking-[0.04em]"
            :class="group.label === '' ? 'italic text-text-muted' : 'text-text-primary'"
          >{{ displayLabel }}</span>
        </div>
        <input
          v-else
          ref="nameInput"
          v-model="nameField.draft.value"
          type="text"
          maxlength="40"
          class="min-w-0 flex-1 rounded-sm border border-accent bg-bg px-2 py-1 font-display text-sm font-semibold tracking-[0.04em] text-text-primary outline-none focus:border-accent"
          :aria-label="messages.group.rename(group.label === '' ? index : group.label)"
          @keydown.enter="nameField.commitEdit"
          @keydown.escape="nameField.cancelEdit"
          @blur="nameField.commitEdit"
        />
        <span
          v-if="isUnlimited"
          class="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-bg-surface px-2 py-0.5 font-mono text-xs"
        >
          <span class="text-text-primary">{{ group.playerIds.length }}</span>
          <span class="text-text-muted">/</span>
          <span class="text-text-muted" :aria-label="messages.group.unlimited">∞</span>
        </span>
        <span
          v-else
          class="inline-flex shrink-0 items-center gap-1 rounded-full border bg-bg-surface px-2 py-0.5 font-mono text-xs transition-[border-color,color] duration-120 ease-standard"
          :class="isOffTarget ? 'border-danger/60 text-danger' : 'border-border'"
          :aria-invalid="isOffTarget ? 'true' : undefined"
        >
          <span v-if="isOffTarget" class="sr-only">{{ messages.group.capacityError }}</span>
          <span :class="isOffTarget ? 'text-danger' : 'text-text-primary'">{{ group.playerIds.length }}</span>
          <span class="text-text-muted">/</span>
          <input
            v-if="canEdit"
            type="number"
            class="w-7 appearance-textfield bg-transparent p-0 text-center transition-colors duration-120 ease-standard [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus-visible:outline-none"
            :class="isOffTarget ? 'text-danger' : 'text-text-primary'"
            :min="MIN_MAX_PLAYERS"
            :max="MAX_MAX_PLAYERS"
            :value="group.maxPlayers"
            :aria-label="messages.group.maxPlayers(displayLabel)"
            @change="onMaxInput"
          />
          <span v-else class="font-mono text-xs" :class="isOffTarget ? 'text-danger' : 'text-text-muted'">{{ group.maxPlayers }}</span>
        </span>
        <AppButton
          :variant="ButtonVariant.Ghost"
          :size="ButtonSize.Sm"
          :disabled="mode === BoardMode.Raid && store.activeRaidEditLocked"
          :aria-label="group.collapsed ? messages.group.expand(displayLabel) : messages.group.collapse(displayLabel)"
          :aria-expanded="!group.collapsed"
          :aria-controls="groupBodyId"
          :title="group.collapsed ? messages.group.expand(displayLabel) : messages.group.collapse(displayLabel)"
          @click="toggleCollapsed"
        >
          <template #icon-left>
            <AppIcon
              :name="group.collapsed ? IconName.ChevronDown : IconName.ChevronUp"
            />
          </template>
        </AppButton>
        <AppButton
          v-if="canManageRoster"
          :variant="ButtonVariant.Ghost"
          :size="ButtonSize.Sm"
          :aria-label="messages.group.selectPlayers(displayLabel)"
          :title="messages.group.selectPlayers(displayLabel)"
          @click="emit('open-player-selector', group.id)"
        >
          <template #icon-left><AppIcon :name="IconName.Plus" :size="15" /></template>
        </AppButton>
      </div>
    </header>

    <div
      v-show="!group.collapsed"
      :id="groupBodyId"
      class="flex min-w-0 flex-1 flex-col"
    >
      <PlayerDropZone
        :group="group"
        :players="players"
        :enabled="canEdit || canEditRaid"
        :board-mode="mode"
        :grid="playersAsGrid"
        :player-filters-active="playerFiltersActive"
        :player-filter-mode="playerFilterMode"
        :matching-player-ids="matchingPlayerIds"
        @drag-add="onDragAdd"
        @drag-end="onDragEnd"
        @use-build="(id, bi) => emit('use-build', id, bi)"
        @add-build="(id) => emit('add-build', id)"
        @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
        @add-guild-build="emit('add-guild-build')"
        @edit-guild-build="(id, buildId, slot) => emit('edit-guild-build', id, buildId, slot)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useInlineEditableTextField } from '@/shared/composables/useInlineEditableTextField'
import { session } from '@/app/session'
import { AppButton, AppIcon, ButtonSize, ButtonVariant, IconName } from '@/shared/ui'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import PlayerDropZone from './PlayerDropZone.vue'
import { PlayerFilterMode } from '../../lib/playerFilters'
import type { PlayerFilterMode as PlayerFilterModeType } from '../../lib/playerFilters'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { PlayerGroup as PlayerGroupType, Player } from '../../types'
import { BoardMode } from '../../types'

const MIN_MAX_PLAYERS = 1
const MAX_MAX_PLAYERS = 10

export interface PlayerGroupProps {
  readonly group: PlayerGroupType
  readonly mode?: BoardMode
  readonly playersAsGrid?: boolean
  readonly playerFiltersActive?: boolean
  readonly playerFilterMode?: PlayerFilterModeType
  readonly matchingPlayerIds?: ReadonlySet<string>
}

const props = withDefaults(defineProps<PlayerGroupProps>(), {
  mode: BoardMode.Live,
  playersAsGrid: false,
  playerFiltersActive: false,
  playerFilterMode: PlayerFilterMode.Highlight,
  matchingPlayerIds: () => new Set<string>(),
})

const emit = defineEmits<{
  (event: 'use-build', playerId: string, buildIndex: number): void
  (event: 'add-build', playerId: string): void
  (event: 'use-guild-build', playerId: string, buildId: string): void
  (event: 'add-guild-build'): void
  (event: 'edit-guild-build', playerId: string, buildId: string, slot: 0 | 1): void
  (event: 'open-player-selector', groupId: string): void
}>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
/**
 * Live-mode rename / DnD gate. Pure officer-only; the live board uses a
 * stricter role gate because the surface reflects the canonical guild
 * record (unlike raid snapshots, which are blameless to experiment with).
 */
const canEdit = computed<boolean>(() => store.isOfficer && props.mode === BoardMode.Live)

/**
 * Raid-mode DnD gate. Mirrors the backend `require_snapshot_player_or_officer`
 * helper in `apps/backend/api/src/raid_mutations.rs`: the caller is
 * allowed when they are an officer/owner of the active guild OR when
 * their Discord id matches any raid-player in this group. A group with
 * no matching player and no officer becomes read-only — that's the same
 * contract `f_raid_player_move` enforces server-side, so the UI surface
 * can't fire a mutation that the resolver will reject.
 */
const canEditRaid = computed<boolean>(() => {
  if (props.mode !== BoardMode.Raid) return false
  if (store.activeRaidEditLocked) return false
  if (store.isOfficer || store.isOwner) return true
  const me = session.value?.discordUserId
  if (me === undefined || me === '') return false
  return props.group.playerIds.some((raidPlayerId) => {
    const raidPlayer = store.raidPlayers.find((rp) => rp.id === raidPlayerId)
    return raidPlayer?.discordUserId === me
  })
})
const canManageRoster = computed<boolean>(
  () => (store.isOfficer || store.isOwner)
    && (props.mode === BoardMode.Live
      || (props.mode === BoardMode.Raid && !store.activeRaidEditLocked)),
)
const players = computed<ReadonlyArray<Player>>(() => store.currentPlayers)

const parentRoom = computed(() =>
  store.currentRooms.find((room) => room.groupIds.includes(props.group.id)),
)

const index = computed<number>(() => {
  const position = parentRoom.value?.groupIds.indexOf(props.group.id) ?? -1
  return position >= 0 ? position + 1 : 1
})

const isOffTarget = computed<boolean>(() => {
  if (parentRoom.value?.noPlayerLimit === true) return false
  return props.group.playerIds.length !== props.group.maxPlayers
})

const isUnlimited = computed<boolean>(() => parentRoom.value?.noPlayerLimit === true)
const displayLabel = computed<string>(() => props.group.label || messages.value.group.fallbackName(index.value))
const currentLabel = computed<string>(() => props.group.label)
const groupBodyId = computed<string>(() => `planner-group-${props.group.id}-body`)

const nameField = useInlineEditableTextField({
  currentValue: currentLabel,
  field: 'groupName',
  canEdit,
  validate: (value) => value !== messages.value.group.fallbackName(index.value),
  onCommit: (value) => {
    store.renameGroup(props.group.id, value === '' ? '' : value)
  },
})

function onDragAdd(playerId: string, index: number): void {
  if (!canEdit.value && !canEditRaid.value) return
  void store.movePlayerToGroup(playerId, props.group.id, index)
}

function onDragEnd(orderedIds: ReadonlyArray<string>): void {
  if (!canEdit.value && !canEditRaid.value) return
  void store.reorderGroup(props.group.id, [...orderedIds])
}

function onMaxInput(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLInputElement)) return
  const raw = Number(target.value)
  if (Number.isNaN(raw)) return
  store.setMaxPlayers(props.group.id, raw)
}

function toggleCollapsed(): void {
  if (props.mode === BoardMode.Raid && store.activeRaidEditLocked) return
  store.setGroupCollapsed(props.group.id, !props.group.collapsed)
}
</script>
